import Button from 'ext-ui/components/Button/Button'
import FileDisplay from 'ext-ui/components/FileDisplay/FileDisplay'
import React, { useCallback, useEffect, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Dropzone from 'src/components/AddAttachment/Dropzone'
import ErrorList from 'src/components/ErrorList/ErrorList'
import { usePostExpensePolicy } from 'src/components/ExpensePolicyUploadBox/expensePolicy.queries'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import { usePostManagerReviewRules } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useTranslate } from 'src/localization/Trns'
import {
  ExpensePolicyResponse,
  ManagerApprovalRulesResponse,
} from 'src/models/organizations.models'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { parseBackendErrors } from 'src/utils/error.utils'

interface UploadedFile {
  formData?: FormData
  uploadId?: string
  fileName: string
  urls: {
    original: string
    thumb: string
  }
}

interface ExpensePolicyUploadBoxFormProps {
  closeForm: () => void
  rules: ManagerApprovalRulesResponse | undefined
  policy?: ExpensePolicyResponse
  organizationId: string
}

const ExpensePolicyUploadBoxForm: React.FC<ExpensePolicyUploadBoxFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const { rules, policy, closeForm, organizationId } = props

  const { mutate: postExpensePolicy, isPending: uploadingPolicy } =
    usePostExpensePolicy({
      orgId: organizationId,
    })

  const {
    mutate: postRules,
    isPending: submittingRules,
    error: postRulesError,
  } = usePostManagerReviewRules()
  const rulesErrors = postRulesError
    ? parseBackendErrors(postRulesError)
    : undefined

  const saving = submittingRules || uploadingPolicy

  const [policyAssistantEnabled, setPolicyAssistantEnabled] = useState<boolean>(
    rules?.policyAssistantEnabled || false,
  )
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | undefined>()
  const [error, setError] = useState<string | React.ReactElement | undefined>()

  useEffect(() => {
    if (rules) {
      setPolicyAssistantEnabled(rules.policyAssistantEnabled || false)
    }
    if (policy) {
      setUploadedFile(policy)
    }
  }, [rules, policy])

  const postExpensePolicyEnabled = () => {
    if (policyAssistantEnabled !== rules?.policyAssistantEnabled) {
      postRules(
        {
          body: {
            policyAssistantEnabled: policyAssistantEnabled,
          },
        },
        {
          onSuccess: () => {
            closeForm()
            analytics?.track('org settings edit policy assistant rules', {
              policyAssistantEnabled: policyAssistantEnabled,
              hasUploadedFile: !!uploadedFile,
            })
          },
        },
      )
    } else {
      closeForm()
    }
  }

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (!policyAssistantEnabled) {
      // If disabling, no need to update the policy file
      postExpensePolicyEnabled()
    } else {
      if (!uploadedFile) {
        // If enabled, ensure file is uploaded else show error
        setError(
          translate(
            'Please upload an expense policy document to enable the AI Policy Assistant.',
          ),
        )
      } else {
        // Enabled and has uploaded file
        if (uploadedFile.formData !== undefined) {
          // new policy is uploaded; save it
          postExpensePolicy(
            { body: uploadedFile.formData },
            {
              onSuccess: () => {
                // After successful file upload, update the rules
                postExpensePolicyEnabled()
              },
            },
          )
        } else {
          // policy is the existing policy; just update the rules
          postExpensePolicyEnabled()
        }
      }
    }
  }

  const handleFileUpload = useCallback(
    (attachments: PostReceiptAttachmentRequest[]): void => {
      if (attachments.length > 0) {
        const attachment = attachments[0]
        const file = attachment.formData.get('file') as File
        setUploadedFile({
          fileName: file.name,
          formData: attachment.formData,
          uploadId: attachment.uploadId,
          urls: {
            original: attachment.thumbnailUrl,
            thumb: attachment.thumbnailUrl,
          },
        })
        setError(undefined)
      }
    },
    [],
  )

  return (
    <form className={'flex-column'} onSubmit={handleOnSubmit}>
      <div className={'flex-column gap-10'}>
        <div className={'flex-column gap-9'}>
          <div className={'flex-column gap-2'}>
            <span className={'font-form-label'}>
              {translate('AI Policy Assistant')}
            </span>
            <div className={'flex-row'}>
              <OnOffSwitch
                defaultOnOffState={!!policyAssistantEnabled}
                onChange={(val: boolean): void => {
                  setPolicyAssistantEnabled(val)
                }}
                testId={'receiptRulesSwitch'}
              />
            </div>
          </div>

          {policyAssistantEnabled && (
            <div className={'flex-column gap-2'}>
              <span className={'font-form-label flex-row align-center gap-1'}>
                {translate('Expense Policy')}
                <span className={'required-asterisk'}>{'*'}</span>
              </span>
              <div className={'flex-column gap-2 w-full'}>
                {uploadedFile === undefined && (
                  <Dropzone
                    uploadAttachments={handleFileUpload}
                    isAttachmentsListEmpty={true}
                    attachmentLimit={1}
                    setAttachmentError={setError}
                    testId={'expense-policy-dropzone'}
                    attachmentsText={
                      <div className={'flex-column align-center gap-2'}>
                        <span>{translate('+ Upload Expense Policy')}</span>
                        <span className={'font-sm text-secondary'}>
                          {translate('PDF, SVG, PNG, JPG or GIF (max. 10MB)')}
                        </span>
                      </div>
                    }
                  />
                )}
                {uploadedFile && (
                  <FileDisplay
                    {...uploadedFile}
                    removeFile={() => setUploadedFile(undefined)}
                    data-testid={'expensePolicy'}
                  />
                )}
                {error && (
                  <ErrorList
                    className={'TaskDetails-missingReceipts-errorList'}
                    errors={[error]}
                  />
                )}
              </div>
            </div>
          )}
          {rulesErrors?.topLevelError !== undefined && (
            <div className={'font-error-message'}>
              {rulesErrors.topLevelError}
            </div>
          )}
        </div>
        <div className={'flex-row gap-5 align-center justify-start'}>
          <Button
            variant={'primary'}
            type={'submit'}
            loading={saving}
            disabled={saving}
            aria-label={translate('Save')}
            data-testid={'saveRulesBtn'}
          >
            {translate('Save')}
          </Button>
          <Button
            variant={'text'}
            onClick={closeForm}
            aria-label={translate('Cancel')}
            data-testid={'cancelRulesBtn'}
          >
            {translate('Cancel')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default ExpensePolicyUploadBoxForm
